/**
 * Shared hidden-field helpers for the two search Typeforms: the relevance
 * survey (`SearchFeedbackDialog`, embedded widget) and the zero-results form
 * (`EmptySearchResultsNewUI`, plain link). Both record `user_id`,
 * `search_query`, `search_id` and `filter_applied`, so the filter state must be
 * serialized identically on both sides to stay comparable.
 */
import { searchFilterKeys } from '../constants';
import type { SearchFilters } from '../types/search';

const getAppliedFilters = (filters: SearchFilters): string[] => {
	const applied: string[] = [];

	for (const key of searchFilterKeys) {
		if (key === 'classYear' && filters.minClassYear != null && filters.maxClassYear != null) {
			applied.push(`classYear: ${filters.minClassYear}-${filters.maxClassYear}`);
			continue;
		}
		if (key === 'inclusionAdapted' && filters.inclusionAdapted) {
			applied.push('inclusionAdapted: true');
			continue;
		}
		const value = filters[key as keyof SearchFilters];
		if (Array.isArray(value) && value.length > 0) {
			applied.push(`${key}: ${value.join(', ')}`);
		}
	}

	return applied;
};

export const getFilterAppliedHiddenField = (filters?: SearchFilters): string =>
	JSON.stringify(filters ? getAppliedFilters(filters) : []);

type ZeroResultsFeedbackFields = {
	userId?: string;
	searchTerm?: string;
	searchId?: string;
	filterApplied?: string;
};

/**
 * Fills the `{…}` placeholders of the configured zero-results Typeform URL
 * (`newUIZeroResultsModal.url`, hidden fields in the `#` fragment). A field the
 * page cannot supply becomes empty rather than a literal placeholder — Typeform
 * ignores empty hidden fields.
 */
export const buildZeroResultsFeedbackUrl = (
	url: string,
	{ userId, searchTerm, searchId, filterApplied }: ZeroResultsFeedbackFields
): string =>
	url
		.replace('{userId}', encodeURIComponent(userId ?? ''))
		.replace('{searchTerm}', encodeURIComponent(searchTerm ?? ''))
		.replace('{searchId}', encodeURIComponent(searchId ?? ''))
		.replace('{filterApplied}', encodeURIComponent(filterApplied ?? ''));
